import { useRef, useReducer, useEffect, useMemo, useState } from 'react';
import { INITIAL_FORM_STATE, formReducer } from '../state/formReducer.js';
import { prepareFormOptions } from '../utils/formOptions.js';
import {
  createSignupPayload,
  fetchSignupConfig,
  submitSignupData,
} from '../utils/api';
import {
  createSignupSchema,
  createPasswordRules,
} from '../utils/validation.js';
import * as v from 'valibot';
import {
  getTimezoneKeyByOlsonName,
  getCountryByRegionalization,
  getCurrencyCodeByCountryCode,
  getReferralId,
  getMarketingConsent,
} from '../utils/helpers.js';
import { fireSignupTrackingEvents } from '../../../../tracking/trackers/trackerComposite';

export function useSignupForm({ dataset, umbraco }) {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco));
  umbraco ||= umbracoRef.current;
  const swiperRef = useRef(null);
  const formRef = useRef(null);

  const [state, dispatch] = useReducer(formReducer, INITIAL_FORM_STATE);
  const { validationErrors } = state;

  // State for API data and loading
  const [configData, setConfigData] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch configuration data on component mount
  useEffect(() => {
    async function loadConfig() {
      try {
        setIsLoading(true);
        setConfigError(null);
        const data = await fetchSignupConfig();
        setConfigData(data);
      } catch (error) {
        console.error('Failed to load configuration:', error);
        setConfigError(error.message);
      } finally {
        setIsLoading(false);
      }
    }

    loadConfig();
  }, []);

  // Prepare form options from API data
  const formOptions = useMemo(() => {
    if (!configData) return null;
    return prepareFormOptions(configData);
  }, [configData]);

  const showComponent = !isLoading && !configError && !!formOptions;

  // Initialize form with browser timezone and regionalization data
  useEffect(() => {
    if (typeof window !== 'undefined' && showComponent && formOptions) {
      const initializeTimezone = async () => {
        try {
          const timezoneKey = await getTimezoneKeyByOlsonName(
            formOptions.rawData.time_zones,
          );
          if (timezoneKey) {
            dispatch({
              type: 'FIELD_CHANGE',
              field: 'timeZone',
              value: timezoneKey,
            });
          }
        } catch (error) {
          console.warn('Failed to initialize timezone:', error);
        }
      };

      initializeTimezone();

      const countryCode = getCountryByRegionalization();
      if (countryCode) {
        dispatch({
          type: 'FIELD_CHANGE',
          field: 'countryCode',
          value: countryCode,
        });
        dispatch({
          type: 'FIELD_CHANGE',
          field: 'currency',
          value: getCurrencyCodeByCountryCode(
            countryCode,
            formOptions.countries,
          ),
        });
      }

      const marketingConsent = getMarketingConsent(
        formOptions.rawData.sign_up_marketing_consent,
      );
      dispatch({
        type: 'FIELD_CHANGE',
        field: 'marketingConsent',
        value: marketingConsent,
      });
      dispatch({
        type: 'FIELD_CHANGE',
        field: 'consentTemplateId',
        value: marketingConsent.consentTemplateId || null,
      });

      dispatch({
        type: 'FIELD_CHANGE',
        field: 'referralId',
        value: formOptions.referralId,
      });
    }
  }, [showComponent, formOptions]);

  const referralId = getReferralId();
  const signupSchema = createSignupSchema();
  const passwordRules = createPasswordRules();

  // Navigation handlers
  const handlePrevious = () => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current?.swiper) {
      setTimeout(function () {
        swiperRef.current.swiper.slideNext();
      }, 250);
    }
  };

  const handlePrev = () => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  const handleOtherSoftwareChange = (event) => {
    dispatch({
      type: 'FIELD_CHANGE',
      field: 'otherSoftware',
      value: event.target.value,
    });
    if (event.target.value) handleNext();
  };

  // Form submission
  const handleStartTrial = async (event) => {
    event.preventDefault();

    dispatch({
      type: 'SET_FORM_STATE',
      payload: { apiError: '', validationErrors: [] },
    });
    if (!formRef.current) return;

    const validationResult = v.safeParse(signupSchema, state);

    if (!validationResult.success) {
      const errorMessages = validationResult.issues.map(
        (issue) => issue.message,
      );
      dispatch({ type: 'VALIDATION_FAILED', errors: errorMessages });
      return;
    }

    // Trigger Marketo form submission by setting isMutating to true
    dispatch({ type: 'SET_IS_MUTATING', value: true });

    const payload = createSignupPayload(state, formOptions);

    try {
      const response = await submitSignupData(payload);
      dispatch({ type: 'SET_FORM_SUCCESS', value: response.success });
      if (!response.success) {
        throw response;
      }

      if (response.data?.login_url) {
        dispatch({
          type: 'FIELD_CHANGE',
          field: 'loginUrl',
          value: response.data.login_url,
        });
      }

      await fireSignupTrackingEvents(payload, state.teamSize);
    } catch (error) {
      const errorMessage =
        error?.message || 'Something went wrong. Please try again.';
      dispatch({ type: 'SET_API_ERROR', value: errorMessage });
      console.error('Error submitting signup data:', error);
    } finally {
      dispatch({ type: 'SET_IS_MUTATING', value: false });
    }
  };

  // Error handling helpers
  const hasFieldError = (fieldName) => {
    return validationErrors.some((error) =>
      error.toLowerCase().includes(fieldName.toLowerCase()),
    );
  };

  const getFieldError = (fieldName) => {
    const error = validationErrors.find((error) =>
      error.toLowerCase().includes(fieldName.toLowerCase()),
    );
    return error || '';
  };

  const getGeneralValidationErrors = () => {
    const knownFields = [
      'first name',
      'last name',
      'business name',
      'phone number',
      'email',
      'password',
      'promo code',
      'country',
      'currency',
      'timeZone',
      'recaptcha',
    ];

    return validationErrors.filter(
      (error) =>
        !knownFields.some((field) => error.toLowerCase().includes(field)),
    );
  };

  return {
    umbraco,
    swiperRef,
    formRef,
    state,
    dispatch,
    passwordRules,
    handlePrevious,
    handleNext,
    handlePrev,
    handleOtherSoftwareChange,
    handleStartTrial,
    hasFieldError,
    getFieldError,
    getGeneralValidationErrors,
    referralId,
    showComponent,
    configError,
    isLoading,
    formOptions,
  };
}
